import { logger } from '../../utils/Logger';
import { _Math } from '../../math/Math';
import type { TypedArray } from '../../utils/Utils';
import {
    getFormatByteSize,
    LegacySourceTexture,
    type SourceTextureLayerWebGLUploadResult,
    type SourceTextureWebGLUploadResult,
    TextureMipmapGroup,
    type WebGLTextureUploadCtx,
    getInternalFormat,
    type WebGLUploadable,
} from './Texture';
import { ContentBridge } from '../../ContentAPI';
import { WebGLPixelFormat } from '../../renderer/webgl/WGLConstants';
import { TextureDataType } from '../../utils/Constants';
import { RendererBackend } from '../../renderer/IRenderer';
import { TextureDimension, TextureViewDimension } from './types';

export class Texture3D extends LegacySourceTexture {
    readonly is3d = true;
    source: TextureMipmapGroup<Texture3DLayer>;

    constructor(source: TextureMipmapGroup<Texture3DLayer>) {
        super(TextureDimension.D3, TextureViewDimension.D3, false);
        ContentBridge.textureCreate(this);
        this.source = source;
        this.source.syncData(this);
    }

    static createByMainLayer(layer: Texture3DLayer) {
        return new Texture3D(TextureMipmapGroup.create(layer));
    }

    static createByMainLayerSource(
        source: Texture3DLayerSource,
        format: WebGLPixelFormat = WebGLPixelFormat.RGBA,
        type: TextureDataType = TextureDataType.UnsignedByteType,
        width: number,
        height: number,
        depth: number,
    ) {
        return new Texture3D(
            TextureMipmapGroup.create(
                Texture3DLayer.create(source).setSize(width, height, depth).setFormat(format).setType(type),
            ),
        );
    }

    className() {
        return 'Texture3D';
    }

    /**
     * @internal
     */
    uploadWebGLImpl(
        ctx: WebGLTextureUploadCtx,
        disableCustomMipmap: boolean,
        needPOT: boolean,
    ): SourceTextureWebGLUploadResult {
        return this.source.uploadWebGL(ctx, ctx.gl.TEXTURE_3D, disableCustomMipmap, needPOT);
    }

    /**
     * @internal
     */
    canAutoGenerateMipmap(): boolean {
        return true;
    }

    protected getAutoGeneratedMipmapByteSize() {
        const main = this.source.main;
        if (main) {
            return main.getAutoGeneratedMipmapFromCurrentLevel();
        }
        return 0;
    }
}

type Texture3DLayerSource = TypedArray;

/**
 * Upload layer for 3D texture data.
 */
export class Texture3DLayer implements WebGLUploadable {
    __brand: 'WebGLUploadable';

    source: Texture3DLayerSource;

    format: WebGLPixelFormat;
    type: TextureDataType;

    width: number;
    height: number;
    depth: number;

    constructor(source: Texture3DLayerSource) {
        this.source = source;
    }

    static create(source: Texture3DLayerSource) {
        return new Texture3DLayer(source);
    }

    setFormat(format: WebGLPixelFormat) {
        this.format = format;
        return this;
    }

    setType(type: TextureDataType) {
        this.type = type;
        return this;
    }

    setSize(width: number, height: number, depth: number) {
        this.width = width;
        this.height = height;
        this.depth = depth;
        return this;
    }

    /**
     * @internal
     */
    uploadWebGL(ctx: WebGLTextureUploadCtx, target: number, level: number): SourceTextureLayerWebGLUploadResult {
        if (ctx.backend !== RendererBackend.WEBGL_JS) {
            ctx.gl.texImage3D(
                target,
                level,
                getInternalFormat(ctx.gl, this.format, this.type),
                this.width,
                this.height,
                this.depth,
                0,
                this.format,
                this.type,
                this.source,
            );
        } else {
            logger.unsupported('Unsupported 3d tex in webgl1');
        }
        const texelCount = this.width * this.height * this.depth;
        const texelByte = getFormatByteSize(this.format, this.type);
        const is_pot =
            _Math.isPowerOfTwo(this.width) && _Math.isPowerOfTwo(this.height) && _Math.isPowerOfTwo(this.depth);
        return {
            is_pot,
            byteSize: texelCount * texelByte,
        };
    }

    /**
     * @internal
     */
    getAutoGeneratedMipmapFromCurrentLevel() {
        const { width, height, depth, format, type } = this;
        const texelByte = getFormatByteSize(format, type);
        const levels = 1 + Math.floor(Math.log2(Math.max(width, height, depth)));
        let texelCount = 0;

        for (let i = 0, j = 1; i < levels; i++, j *= 2) {
            texelCount +=
                Math.max(Math.floor(width / j), 1) *
                Math.max(Math.floor(height / j), 1) *
                Math.max(Math.floor(depth / j), 1);
        }

        return texelCount * texelByte;
    }
}
